import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Zap, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, formatApiError } from "@/lib/api";
import { formatDateTime, onlyDigits } from "@/lib/format";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { TextFormatBar } from "@/components/admin/TextFormatBar";

const STATUS = { AGUARDANDO: ["Aguardando cliente", "text-amber-300"], EXIBINDO: ["Mensagem na tela do cliente", "text-amber-300"], VALIDANDO: ["Aguardando sua validação", "text-sky-300"], CONCLUIDO: ["Concluída", "text-emerald-300"], BLOQUEADO: ["Bloqueada: tentativas esgotadas", "text-red-300"] };

export const ReviewButtons = ({ onReview, testPrefix }) => (
  <div className="mt-2 flex gap-2">
    <button onClick={() => onReview(true)} className="btn-gold h-8 px-3 text-xs" data-testid={`${testPrefix}-approve`}><Check className="h-3.5 w-3.5" /> Aprovar</button>
    <button onClick={() => onReview(false)} className="btn-ghost-sm" data-testid={`${testPrefix}-reject`}><X className="h-3.5 w-3.5" /> Recusar</button>
  </div>
);

const CurrentAction = ({ ca, onCancel, onReview, onComplete }) => {
  const msg = ca.type === "MENSAGEM" && ca.status === "AGUARDANDO";
  const [label, cls] = STATUS[msg ? "EXIBINDO" : ca.status] || [ca.status, ""];
  return (
    <div className="mb-3 rounded-sm border border-white/10 bg-black/20 p-3 text-sm" data-testid="custom-action-status">
      <p className="text-slate-200">{ca.name} · <span className={cls} data-testid="custom-action-status-label">{label}</span></p>
      {ca.response && <p className="mt-1 text-slate-100" data-testid="custom-action-response">Resposta: <span className="font-mono-x">{ca.response}</span></p>}
      <p className="mt-1 text-xs text-slate-500">{ca.type === "CODIGO" ? `${ca.attempts} tentativa(s) incorreta(s) · ` : ""}{formatDateTime(ca.requested_at)} · {ca.operator?.name}</p>
      {ca.status === "VALIDANDO" && <ReviewButtons onReview={onReview} testPrefix="custom-action" />}
      {msg && <button onClick={onComplete} className="btn-gold mt-2 mr-2 h-8 px-3 text-xs" data-testid="custom-action-complete"><Check className="h-3.5 w-3.5" /> Concluir mensagem</button>}
      {!msg && !["CONCLUIDO", "VALIDANDO"].includes(ca.status) && <button onClick={onCancel} className="btn-ghost-sm mt-2" data-testid="custom-action-cancel"><X className="h-3.5 w-3.5" /> Cancelar</button>}
    </div>
  );
};

export const CustomActionButtons = ({ req, onChanged }) => {
  const [actions, setActions] = useState([]);
  const [codeFor, setCodeFor] = useState(null);
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const textRef = useRef(null);
  const [align, setAlign] = useState("center");
  const open = (a) => { setCode(""); setText(a.description || ""); setAlign(a.text_align || "center"); setCodeFor(a); };

  const load = useCallback(() => http.get("/admin/actions").then(({ data }) => setActions(data.filter((a) => a.active))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useRealtimeEvent((m) => { if (m.event === "actions.updated") load(); });

  const trigger = async (a, body = {}) => {
    try {
      await http.post(`/admin/requests/${req.id}/actions/${a.id}/trigger`, body);
      toast.success(`"${a.name}" enviada ao cliente.`);
      setCodeFor(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async () => {
    try { await http.post(`/admin/requests/${req.id}/actions/cancel`); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const complete = async () => {
    try { await http.post(`/admin/requests/${req.id}/actions/complete`); toast.success("Mensagem concluída."); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const review = async (approve) => {
    try { await http.post(`/admin/requests/${req.id}/actions/review`, { approve }); toast.success(approve ? "Resposta aprovada." : "Resposta recusada; o cliente enviará novamente."); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-3 border-t border-white/10 pt-3" data-testid="custom-actions">
      {req.custom_action && <CurrentAction ca={req.custom_action} onCancel={cancel} onReview={review} onComplete={complete} />}
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button key={a.id} onClick={() => open(a)} className="btn-ghost-sm" data-testid={`custom-action-btn-${a.id}`}>
            <Zap className="h-3.5 w-3.5" /> {a.name}
          </button>
        ))}
        {actions.length === 0 && <p className="text-xs text-slate-500">Crie novas ações em Configurações &gt; Funcionalidades.</p>}
      </div>
      {req.actions_done?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-slate-400" data-testid="custom-actions-done">
          {req.actions_done.slice().reverse().map((d) => (
            <li key={d.instance_id}>✓ {d.name}{d.response ? `: ${d.response}` : ""} · {formatDateTime(d.completed_at)}</li>
          ))}
        </ul>
      )}
      <Dialog open={!!codeFor} onOpenChange={(o) => !o && setCodeFor(null)}>
        <DialogContent className="border-white/10 bg-[#141C2E] text-slate-100">
          <DialogHeader>
            <DialogTitle>{codeFor?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Personalize as instruções para este envio. O texto padrão da funcionalidade não será alterado.</DialogDescription>
          </DialogHeader>
          <label className="text-xs text-slate-400">Texto / instruções para o cliente</label>
          {codeFor?.type === "MENSAGEM" && <TextFormatBar textareaRef={textRef} value={text} onChange={setText} align={align} onAlign={setAlign} testPrefix="send-fmt" />}
          <Textarea ref={textRef} value={text} maxLength={1000} rows={5} onChange={(e) => setText(e.target.value)} className="border-white/10 bg-black/30 text-slate-100" data-testid="custom-action-text-input" />
          {codeFor?.type === "CODIGO" && <label className="text-xs text-slate-400">Código de {codeFor?.digits} dígitos enviado ao cliente</label>}
          {codeFor?.type === "CODIGO" && <Input value={code} inputMode="numeric" onChange={(e) => setCode(onlyDigits(e.target.value).slice(0, codeFor?.digits || 8))} className="border-white/10 bg-black/30 font-mono-x tracking-[0.4em] text-slate-100" data-testid="custom-action-code-input" />}
          <DialogFooter>
            <button onClick={() => setCodeFor(null)} className="btn-ghost-sm" data-testid="custom-action-modal-cancel">Cancelar</button>
            <button onClick={() => trigger(codeFor, codeFor.type === "CODIGO" ? { code, description: text } : { description: text, text_align: align })} disabled={codeFor?.type === "CODIGO" && code.length !== codeFor?.digits} className="btn-gold h-9 px-4 text-sm" data-testid="custom-action-send-btn">Enviar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
